import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import InlineNotification from '../inline-notification'

// Shared across every page the banner appears on, so dismissing it on one page dismisses
// it on all of them for the rest of the session (NZTAJP-699 AC5.1) — deliberately not scoped
// per-page or per-region like regionAlertBanner's `${regionSlug}-banner` key.
export const DISCLAIMER_BANNER_DISMISS_KEY = 'nzta-disclaimer-banner-dismissed'

// Both rendered once in JourneysSPA.ss, for every SPA route (NZTAJP-699).
const CONTENT_SELECTOR = '#page-help__journey-planner-disclaimer'
const VISIBILITY_SELECTOR = '#disclaimer-banner'

// Rendered once inside the map-area wrapper (router.tsx) on every route that has a map.
// Portalling the banner here — rather than styling it from wherever it happens to be
// mounted per-page — is what lets the overlay treatment cover only the map pane: the
// anchor's containing block is .l-map-tool__map-area, not the sidebar, so a page's own
// header/tabs stay visible and clickable underneath. Found broken (contained by the
// sidebar instead) while this component was still styled from its per-page mount point.
export const OVERLAY_ANCHOR_ID = 'disclaimer-banner-overlay-anchor'

// Matches breakpoints.tablet-portrait-up (min-width: 600px) and the same
// window.innerWidth > 600 check already used elsewhere (HighwayConditions.tsx,
// TrafficAndTravelListView.tsx) for this breakpoint.
const TABLET_PORTRAIT_UP_MIN_WIDTH = 600

export interface DisclaimerBannerProps {
  // Every page overlays the map at tablet+ regardless of this prop. At mobile,
  // 'in-panel' (the default) flows the banner in normal document flow wherever it's
  // mounted; 'overlay' switches it to the same map-overlay treatment used at tablet+ —
  // needed by pages with no map-view/list-view toggle to switch on (e.g. Traffic cameras).
  mobileTreatment?: 'in-panel' | 'overlay'
}

/**
 * Dismissible disclaimer banner sourcing its copy from SiteConfig.DisclaimerText and its
 * visibility (date window / hide checkbox) from PageController::shouldDisplayDisclaimerBanner(),
 * both exposed via hidden divs in JourneysSPA.ss. Reuses InlineNotification rather than a
 * bespoke visual, per NZTAJP-699's non-goals.
 */
export default function DisclaimerBanner({ mobileTreatment = 'in-panel' }: DisclaimerBannerProps = {}) {
  const [content, setContent] = useState('')
  const [isServerVisible, setIsServerVisible] = useState(false)
  const [isDismissed, setIsDismissed] = useState(true)

  useEffect(() => {
    setContent(document.querySelector<HTMLElement>(CONTENT_SELECTOR)?.innerHTML?.trim() ?? '')
    setIsServerVisible(document.querySelector<HTMLElement>(VISIBILITY_SELECTOR)?.dataset.visible === 'true')
    setIsDismissed(sessionStorage.getItem(DISCLAIMER_BANNER_DISMISS_KEY) === 'true')
  }, [])

  function handleDismiss() {
    sessionStorage.setItem(DISCLAIMER_BANNER_DISMISS_KEY, 'true')
    setIsDismissed(true)
  }

  if (!isServerVisible || !content || isDismissed) {
    return null
  }

  const isOverlay = mobileTreatment === 'overlay' || window.innerWidth > TABLET_PORTRAIT_UP_MIN_WIDTH

  const bannerClasses = classNames('c-disclaimer-banner', {
    'c-disclaimer-banner--overlay': isOverlay,
  })

  const banner = (
    <div className={bannerClasses} aria-live='polite'>
      <InlineNotification messageType='disclaimer' isDismissable onDismiss={handleDismiss}>
        <span dangerouslySetInnerHTML={{ __html: content }} />
      </InlineNotification>
    </div>
  )

  if (!isOverlay) {
    return banner
  }

  // router.tsx only renders this anchor for routes with a map. Some routes with no map (e.g.
  // Highway conditions' Table view) render their own copy of the anchor closer to their own
  // content instead (NZTAJP-702), so the banner still pins to the top of the page rather than
  // wherever it happens to be mounted. Routes with neither fall back to rendering in place
  // rather than losing the banner entirely.
  const anchor = document.getElementById(OVERLAY_ANCHOR_ID)
  return anchor ? createPortal(banner, anchor) : banner
}
